import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  ArrowLeft,
  Building2,
  User,
  Phone,
  Mail,
  DollarSign,
  Save,
  Loader2,
  ChevronDown,
  ChevronRight,
  Shield,
  Settings,
  TrendingUp,
  Clock,
  CheckCircle,
  Key,
  CreditCard,
  Users,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AddClientPage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  
  // Collapsible sections
  const [companyOpen, setCompanyOpen] = useState(true);
  const [contactOpen, setContactOpen] = useState(true);
  const [billingOpen, setBillingOpen] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    // Company Info
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    password: '',
    initial_balance: 0,
    // Lead Settings
    receive_cold_calls: true,
    receive_live_transfers: true,
    receive_data_leads: true,
    distribution_mode: 'round_robin',
    distribution_priority: 100,
    daily_lead_limit: '',
    monthly_lead_limit: '',
    // Dialer Settings
    convoso_enabled: false,
    vichi_enabled: false,
    tld_enabled: false,
    // Pricing Overrides
    cold_call_price_override: '',
    live_transfer_price_override: '',
    data_lead_price_override: '',
    // Notes
    admin_notes: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!formData.contact_name.trim()) {
      toast.error('Contact name is required');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!formData.password || formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('dme_token');
      
      // Create the client
      const response = await axios.post(`${API_URL}/api/admin/clients`, {
        company_name: formData.company_name,
        contact_name: formData.contact_name,
        email: formData.email,
        phone: formData.phone || null,
        password: formData.password,
        initial_balance: parseFloat(formData.initial_balance) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const clientId = response.data.id;

      // Update client settings if any non-default values were set
      const settingsUpdate = {};
      
      if (!formData.receive_cold_calls) settingsUpdate.receive_cold_calls = false;
      if (!formData.receive_live_transfers) settingsUpdate.receive_live_transfers = false;
      if (!formData.receive_data_leads) settingsUpdate.receive_data_leads = false;
      if (formData.distribution_mode !== 'round_robin') settingsUpdate.distribution_mode = formData.distribution_mode;
      if (formData.distribution_priority !== 100) settingsUpdate.distribution_priority = parseInt(formData.distribution_priority);
      if (formData.daily_lead_limit) settingsUpdate.daily_lead_limit = parseInt(formData.daily_lead_limit);
      if (formData.monthly_lead_limit) settingsUpdate.monthly_lead_limit = parseInt(formData.monthly_lead_limit);
      if (formData.convoso_enabled) settingsUpdate.convoso_enabled = true;
      if (formData.vichi_enabled) settingsUpdate.vichi_enabled = true;
      if (formData.tld_enabled) settingsUpdate.tld_enabled = true;
      if (formData.cold_call_price_override) settingsUpdate.cold_call_price_override = parseFloat(formData.cold_call_price_override);
      if (formData.live_transfer_price_override) settingsUpdate.live_transfer_price_override = parseFloat(formData.live_transfer_price_override);
      if (formData.data_lead_price_override) settingsUpdate.data_lead_price_override = parseFloat(formData.data_lead_price_override);
      if (formData.admin_notes) settingsUpdate.admin_notes = formData.admin_notes;

      // Only update settings if there are changes
      if (Object.keys(settingsUpdate).length > 0) {
        await axios.put(`${API_URL}/api/admin/clients/${clientId}/settings`, settingsUpdate, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success('Client created successfully');
      navigate('/client-management');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'NC';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-slate-50 dark:bg-slate-950" data-testid="add-client-page">
      {/* Left Sidebar - Client Preview */}
      <div className="w-80 bg-white dark:bg-slate-900 border-r flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b">
          <Button 
            variant="ghost" 
            size="sm" 
            className="mb-3 -ml-2"
            onClick={() => navigate('/client-management')}
            data-testid="back-to-clients-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Clients
          </Button>
          
          {/* Client Avatar & Name Preview */}
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xl font-semibold">
              {getInitials(formData.company_name || 'New Client')}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold">
                {formData.company_name || 'New Client'}
              </h2>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Building2 className="w-3 h-3" />
                <span>{formData.contact_name || 'Contact Name'}</span>
              </div>
            </div>
          </div>
          
          {/* Status Preview */}
          <div className="flex items-center gap-2 mt-3">
            <Badge className="bg-green-500">
              <CheckCircle className="w-3 h-3 mr-1" />
              Active
            </Badge>
            <Badge className="bg-green-500">Available</Badge>
          </div>
        </div>
        
        {/* Scrollable Sidebar Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Contact Section */}
          <Collapsible open={contactOpen} onOpenChange={setContactOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded hover:bg-muted/50">
              <span className="font-semibold text-sm flex items-center gap-2">
                <User className="w-4 h-4" />
                CONTACT
              </span>
              {contactOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{formData.email || 'No email entered'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{formData.phone || 'No phone entered'}</span>
              </div>
            </CollapsibleContent>
          </Collapsible>
          
          {/* Billing Section */}
          <Collapsible open={billingOpen} onOpenChange={setBillingOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded hover:bg-muted/50">
              <span className="font-semibold text-sm flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                BILLING
              </span>
              {billingOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-3">
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Starting Balance</div>
                <p className="font-medium font-mono text-lg">
                  ${parseFloat(formData.initial_balance || 0).toFixed(2)}
                </p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Lead Types</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {formData.receive_cold_calls && <Badge variant="outline" className="text-xs">Cold Calls</Badge>}
                  {formData.receive_live_transfers && <Badge variant="outline" className="text-xs">Live Transfers</Badge>}
                  {formData.receive_data_leads && <Badge variant="outline" className="text-xs">Data Leads</Badge>}
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
          
          {/* API Access Section */}
          <Collapsible>
            <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded hover:bg-muted/50">
              <span className="font-semibold text-sm flex items-center gap-2">
                <Zap className="w-4 h-4" />
                DIALER ACCESS
              </span>
              <ChevronRight className="w-4 h-4" />
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <div className={`w-2 h-2 rounded-full ${formData.convoso_enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span>Convoso</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className={`w-2 h-2 rounded-full ${formData.vichi_enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span>Vichi</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className={`w-2 h-2 rounded-full ${formData.tld_enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span>TLD</span>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
        
        {/* Submit Button at Bottom */}
        <div className="p-4 border-t bg-white dark:bg-slate-900">
          <Button 
            className="w-full" 
            onClick={handleSubmit} 
            disabled={saving}
            data-testid="create-client-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Client
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Tabs */}
        <div className="bg-white dark:bg-slate-900 border-b px-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-12 bg-transparent border-0 p-0 gap-1">
              <TabsTrigger 
                value="company" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                data-testid="tab-company"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Company Info
              </TabsTrigger>
              <TabsTrigger 
                value="lead-settings" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                data-testid="tab-lead-settings"
              >
                <Users className="w-4 h-4 mr-2" />
                Lead Settings
              </TabsTrigger>
              <TabsTrigger 
                value="dialer" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                data-testid="tab-dialer"
              >
                <Phone className="w-4 h-4 mr-2" />
                Dialer Access
              </TabsTrigger>
              <TabsTrigger 
                value="pricing" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                data-testid="tab-pricing"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Custom Pricing
              </TabsTrigger>
              <TabsTrigger 
                value="notes" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                data-testid="tab-notes"
              >
                <Settings className="w-4 h-4 mr-2" />
                Admin Notes
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit}>
            {/* Company Info Tab */}
            {activeTab === 'company' && (
              <div className="space-y-6 max-w-3xl">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Company Information
                    </CardTitle>
                    <CardDescription>
                      Enter the client's company details and primary contact information
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="company_name">Company Name *</Label>
                        <Input
                          id="company_name"
                          value={formData.company_name}
                          onChange={(e) => handleInputChange('company_name', e.target.value)}
                          placeholder="Acme Lead Services"
                          data-testid="input-company-name"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact_name">Contact Name *</Label>
                        <Input
                          id="contact_name"
                          value={formData.contact_name}
                          onChange={(e) => handleInputChange('contact_name', e.target.value)}
                          placeholder="John Smith"
                          data-testid="input-contact-name"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="john@acmeleads.com"
                            className="pl-9"
                            data-testid="input-email"
                            required
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">Used for client portal login</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="phone"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            placeholder="(555) 123-4567"
                            className="pl-9"
                            data-testid="input-phone"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="password">Portal Password *</Label>
                        <div className="relative">
                          <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="password"
                            type="password"
                            value={formData.password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            placeholder="Min. 8 characters"
                            className="pl-9"
                            data-testid="input-password"
                            required
                            minLength={8}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">Client will use this to access their portal</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="initial_balance">Initial Balance ($)</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="initial_balance"
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.initial_balance}
                            onChange={(e) => handleInputChange('initial_balance', e.target.value)}
                            placeholder="0.00"
                            className="pl-9"
                            data-testid="input-balance"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">Pre-paid balance for lead costs</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Lead Settings Tab */}
            {activeTab === 'lead-settings' && (
              <div className="space-y-6 max-w-3xl">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Lead Type Preferences
                    </CardTitle>
                    <CardDescription>
                      Configure which types of leads this client should receive
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <Label className="text-base">Receive Cold Calls</Label>
                        <p className="text-sm text-muted-foreground">Client will receive cold call leads</p>
                      </div>
                      <Switch
                        checked={formData.receive_cold_calls}
                        onCheckedChange={(checked) => handleInputChange('receive_cold_calls', checked)}
                        data-testid="switch-cold-calls"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <Label className="text-base">Receive Live Transfers</Label>
                        <p className="text-sm text-muted-foreground">Client will receive live transfer leads</p>
                      </div>
                      <Switch
                        checked={formData.receive_live_transfers}
                        onCheckedChange={(checked) => handleInputChange('receive_live_transfers', checked)}
                        data-testid="switch-live-transfers"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <Label className="text-base">Receive Data Leads</Label>
                        <p className="text-sm text-muted-foreground">Client will receive data leads</p>
                      </div>
                      <Switch
                        checked={formData.receive_data_leads}
                        onCheckedChange={(checked) => handleInputChange('receive_data_leads', checked)}
                        data-testid="switch-data-leads"
                      />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Distribution Settings
                    </CardTitle>
                    <CardDescription>
                      Configure how leads are distributed to this client
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Distribution Mode</Label>
                        <Select
                          value={formData.distribution_mode}
                          onValueChange={(value) => handleInputChange('distribution_mode', value)}
                        >
                          <SelectTrigger data-testid="select-distribution-mode">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="round_robin">Round Robin</SelectItem>
                            <SelectItem value="type_matching">Type Matching</SelectItem>
                            <SelectItem value="manual">Manual Only</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">How leads are automatically assigned</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="priority">Priority (lower = higher priority)</Label>
                        <Input
                          id="priority"
                          type="number"
                          min="1"
                          value={formData.distribution_priority}
                          onChange={(e) => handleInputChange('distribution_priority', e.target.value)}
                          data-testid="input-priority"
                        />
                        <p className="text-xs text-muted-foreground">Order in round-robin queue</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Lead Limits
                    </CardTitle>
                    <CardDescription>
                      Set daily and monthly caps on lead volume
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="daily_limit">Daily Lead Limit</Label>
                        <Input
                          id="daily_limit"
                          type="number"
                          min="0"
                          placeholder="No limit"
                          value={formData.daily_lead_limit}
                          onChange={(e) => handleInputChange('daily_lead_limit', e.target.value)}
                          data-testid="input-daily-limit"
                        />
                        <p className="text-xs text-muted-foreground">Leave empty for unlimited</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="monthly_limit">Monthly Lead Limit</Label>
                        <Input
                          id="monthly_limit"
                          type="number"
                          min="0"
                          placeholder="No limit"
                          value={formData.monthly_lead_limit}
                          onChange={(e) => handleInputChange('monthly_lead_limit', e.target.value)}
                          data-testid="input-monthly-limit"
                        />
                        <p className="text-xs text-muted-foreground">Leave empty for unlimited</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Dialer Access Tab */}
            {activeTab === 'dialer' && (
              <div className="space-y-6 max-w-3xl">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="w-5 h-5" />
                      Dialer API Access
                    </CardTitle>
                    <CardDescription>
                      Enable dialer integrations for this client. Once enabled, the client can configure their own API keys in their portal.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${formData.convoso_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                          <Zap className="w-5 h-5" />
                        </div>
                        <div>
                          <Label className="text-base">Convoso Dialer</Label>
                          <p className="text-sm text-muted-foreground">Enable Convoso API integration</p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.convoso_enabled}
                        onCheckedChange={(checked) => handleInputChange('convoso_enabled', checked)}
                        data-testid="switch-convoso"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${formData.vichi_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                          <Zap className="w-5 h-5" />
                        </div>
                        <div>
                          <Label className="text-base">Vichi Dialer</Label>
                          <p className="text-sm text-muted-foreground">Enable Vichi API integration</p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.vichi_enabled}
                        onCheckedChange={(checked) => handleInputChange('vichi_enabled', checked)}
                        data-testid="switch-vichi"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${formData.tld_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                          <Zap className="w-5 h-5" />
                        </div>
                        <div>
                          <Label className="text-base">TLD Dialer</Label>
                          <p className="text-sm text-muted-foreground">Enable TLD API integration</p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.tld_enabled}
                        onCheckedChange={(checked) => handleInputChange('tld_enabled', checked)}
                        data-testid="switch-tld"
                      />
                    </div>
                    
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <div className="flex items-start gap-2">
                        <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800 dark:text-amber-200">API Keys Managed by Client</p>
                          <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                            After you enable these integrations, the client will be able to enter their own API keys 
                            through their portal. You are only enabling access, not configuring credentials.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Custom Pricing Tab */}
            {activeTab === 'pricing' && (
              <div className="space-y-6 max-w-3xl">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Custom Lead Pricing
                    </CardTitle>
                    <CardDescription>
                      Override the global pricing for this client. Leave empty to use global pricing.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="cold_call_price">Cold Call Price Override</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="cold_call_price"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Use global"
                            value={formData.cold_call_price_override}
                            onChange={(e) => handleInputChange('cold_call_price_override', e.target.value)}
                            className="pl-9"
                            data-testid="input-cold-call-price"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="live_transfer_price">Live Transfer Price Override</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="live_transfer_price"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Use global"
                            value={formData.live_transfer_price_override}
                            onChange={(e) => handleInputChange('live_transfer_price_override', e.target.value)}
                            className="pl-9"
                            data-testid="input-live-transfer-price"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="data_lead_price">Data Lead Price Override</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="data_lead_price"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Use global"
                            value={formData.data_lead_price_override}
                            onChange={(e) => handleInputChange('data_lead_price_override', e.target.value)}
                            className="pl-9"
                            data-testid="input-data-lead-price"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex items-start gap-2">
                        <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-blue-800 dark:text-blue-200">Global Pricing Reference</p>
                          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                            You can view and edit global pricing in the Client Management page under the "Global Pricing" tab.
                            Custom pricing here will override those defaults for this client only.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Admin Notes Tab */}
            {activeTab === 'notes' && (
              <div className="space-y-6 max-w-3xl">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="w-5 h-5" />
                      Admin Notes
                    </CardTitle>
                    <CardDescription>
                      Internal notes about this client (not visible to the client)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      placeholder="Add any internal notes about this client, special arrangements, or other important information..."
                      value={formData.admin_notes}
                      onChange={(e) => handleInputChange('admin_notes', e.target.value)}
                      className="min-h-[200px]"
                      data-testid="textarea-admin-notes"
                    />
                  </CardContent>
                </Card>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
